import { useState, useMemo, useEffect, useContext } from "react"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { Switch } from "@/components/ui/switch"
import {
  Map as MapIcon,
  MessageSquare,
  Paperclip,
  ArrowUpDown,
  FileText,
  CircleCheck,
  CircleX,
  Circle,
} from "lucide-react"
import {
  formatDistance,
  formatDuration,
  formatCurrency,
  calculateDistanceDiff,
  calculateTimeDiff,
  calculateEuroDiff,
  calculatePercentageDiff,
} from "../../utils/formatUtils"
import type { Participant } from "@/reducers/chatsSlice"
import { OrderData } from "./DeliveryOrderReport"
import FinishAlert from "../maps/FinishAlert"
import OrderDetailsDialog, {
  FormFields,
  LatestDeliveryOrder,
} from "../maps/orderDetails/OrderDetailsDialog"
import { RouteAlert } from "@/reducers/overlaysSlice"
import { useAppSelector } from "@/reducers/hooks"
import { selectAllDriversAccountChats } from "@/reducers/accountChatsSelectors"
import { XmppContext } from "@/stream/xmppClient"
import { isSltExportEnabledVhost } from "@/lib/sltExportHostRules"
import { useTranslation } from "react-i18next"
import { getIntlLocale } from "@/lib/i18n/format"
import { isPDF } from "@/components/cmrReport/Thumbnail"
import { useCmrDocumentPreview } from "@/components/cmrReport/useCmrDocumentPreview"
import ImageGallery from "@/components/photoView/ImageGallery"
import PDFViewer from "@/components/pdfView/PDFViewer"
import { OrderDocumentsDialog } from "./OrderDocumentsDialog"
import {
  useGpsOrderDocuments,
  type GpsOrderDocument,
} from "./useGpsOrderDocuments"

function truncateFilename(
  filename: string | null | undefined,
  maxLength: number = 20,
): string {
  if (!filename) {
    return "—"
  }
  if (filename.length <= maxLength) {
    return filename
  }

  const lastDotIndex = filename.lastIndexOf(".")

  if (lastDotIndex <= 0) {
    return filename.substring(0, maxLength)
  }

  const extension = filename.substring(lastDotIndex)
  const baseName = filename.substring(0, lastDotIndex)

  const availableSpace = maxLength - extension.length - 3 // 3 for "..."

  if (availableSpace <= 0) {
    return filename.substring(0, maxLength)
  }

  return baseName.substring(0, availableSpace) + "..." + extension
}

interface Order {
  id: string
  orderName: string
  date: string
  driverJid: string
  chatJid: string
  endTimestamp: Date
  actualDistance: string
  actualDuration: string
  actualCost: string
  actualDistanceChange: string
  actualDistanceChangePercentage: number | null
  actualDurationChange: string
  actualDurationChangePercentage: number | null
  actualCostChange: string
  actualCostChangePercentage: number | null
  approachDistance: string
  approachDuration: string
  approachCost: string
  revenue: string
  totalDistance: string | null
  fileUrl: string
  apiOrder: OrderData
}

function transformOrderData(
  apiData: OrderData,
  index: number,
  dateLocale: string | undefined,
): Order {
  const actualDistanceChange = calculateDistanceDiff(
    apiData.actual_distance,
    apiData.expected_distance,
  )
  const actualDistanceChangePercentage = calculatePercentageDiff(
    apiData.actual_distance,
    apiData.expected_distance,
  )
  const actualDurationChange = calculateTimeDiff(
    apiData.actual_duration,
    apiData.expected_duration,
  )
  const actualDurationChangePercentage = calculatePercentageDiff(
    apiData.actual_duration,
    apiData.expected_duration,
  )
  const actualCostChange =
    apiData.expected_tolls_sum && apiData.actual_tolls_sum
      ? calculateEuroDiff(apiData.actual_tolls_sum, apiData.expected_tolls_sum)
      : ""

  const actualCostChangePercentage =
    apiData.expected_tolls_sum && apiData.actual_tolls_sum
      ? calculatePercentageDiff(
          apiData.actual_tolls_sum,
          apiData.expected_tolls_sum,
        )
      : null

  const totalDistance =
    apiData.actual_distance != null || apiData.actual_approach_distance != null
      ? (apiData.actual_distance ?? 0) + (apiData.actual_approach_distance ?? 0)
      : null

  return {
    // The row key. File name and end time repeat across orders from external
    // stop syncs (no file, open ones end at the same planned slot), and rows
    // sharing a key stayed on screen across pages.
    id: apiData.delivery_order_id
      ? `order-${apiData.delivery_order_id}`
      : `row-${index}`,
    orderName: apiData.file_name,
    date: new Date(apiData.end_timestamp).toLocaleDateString(dateLocale),
    driverJid: apiData.driver_jid,
    chatJid: apiData.chat_jid,
    endTimestamp: new Date(apiData.end_timestamp),
    actualDistance: formatDistance(apiData.actual_distance),
    actualDuration: formatDuration(apiData.actual_duration),
    actualCost: apiData.actual_tolls_sum
      ? formatCurrency(apiData.actual_tolls_sum)
      : "-",
    actualDistanceChange,
    actualDistanceChangePercentage,
    actualDurationChange,
    actualDurationChangePercentage,
    actualCostChange,
    actualCostChangePercentage,
    approachDistance: formatDistance(apiData.actual_approach_distance),
    approachDuration: formatDuration(apiData.actual_approach_duration),
    approachCost: apiData.actual_approach_tolls_sum
      ? formatCurrency(apiData.actual_approach_tolls_sum)
      : "-",
    revenue: apiData.price_netto_eur
      ? formatCurrency(apiData.price_netto_eur)
      : "-",
    totalDistance: formatDistance(totalDistance),
    fileUrl: apiData.file_url,
    apiOrder: apiData,
  }
}

// The report matches each delivery order with gps's record of its completion;
// only matched rows carry the actual duration, open orders have none.
export function isCompletedOrder(order: OrderData): boolean {
  return order.actual_duration != null
}

// Single source of truth for the FormFields (camelCase) <-> OrderData
// (snake_case) string fields — buildSeedFields and fieldsToOrderPatch are
// exact mirrors of each other, so drive both off one table instead of
// maintaining two field lists in opposite directions.
const STRING_FIELD_MAP: [keyof FormFields, keyof OrderData][] = [
  ["driverName", "driver_name"],
  ["carPlates", "car_plates"],
  ["trailerPlates", "trailer_plates"],
  ["referenceNumber", "ref_number"],
  ["orderNumber", "order_number"],
  ["nip", "customer_nip"],
  ["customerName", "customer_name"],
  ["coordinatorName", "coordinator_name"],
  ["emptyKilometers", "empty_kilometers"],
  ["loadedKilometers", "loaded_kilometers"],
]

export function buildSeedFields(order: OrderData): FormFields {
  return {
    ...(Object.fromEntries(
      STRING_FIELD_MAP.map(([formKey, orderKey]) => [
        formKey,
        order[orderKey] != null ? String(order[orderKey]) : "",
      ]),
    ) as Omit<FormFields, "price" | "priceCurrency">),
    price: order.price_netto_eur != null ? String(order.price_netto_eur) : "",
    priceCurrency: "EUR",
    // Not in the report summary; the dialog fills them from
    // show_latest_delivery_order once it has the order.
    palletExchange: "",
    temperatureMin: "",
    temperatureMax: "",
  }
}

// Routes, tracks and delivery orders all live under the chat (room) JID;
// driver_jid is the chat's driver account, and rooms without one (transics /
// platform science vehicle rooms) only have a made-up JID gps can't resolve.
export function buildReportRouteAlert(order: OrderData): RouteAlert {
  return {
    visible: true,
    alertId: "report-adhoc",
    driverJid: order.chat_jid,
    updatedAt: order.end_timestamp,
    type: "routeFinished" as const,
    details: {
      alertId: "report-adhoc",
      expectedDistance: order.expected_distance,
      actualDistance: order.actual_distance,
      firstStopId: order.first_stop_id,
      lastStopId: order.last_stop_id,
      routeTimestamp: order.route_timestamp,
      // The version gps planned the order from - the one at route_timestamp
      // may not carry the order's stops (orders from external stop syncs).
      expectedRouteDriverJid: order.expected_route_driver_jid ?? undefined,
      expectedRouteTimestamp: order.expected_route_timestamp ?? undefined,
      expectedRouteFirstStopId: order.expected_route_first_stop_id ?? undefined,
      expectedRouteLastStopId: order.expected_route_last_stop_id ?? undefined,
      startTimestamp: order.start_timestamp,
      endTimestamp: order.end_timestamp,
    },
  } as RouteAlert
}

// Reverse of buildSeedFields — patches the report row after a dialog save so
// the table never shows stale values next to freshly-saved ones.
function fieldsToOrderPatch(fields: FormFields): Partial<OrderData> {
  return {
    ...(Object.fromEntries(
      STRING_FIELD_MAP.map(([formKey, orderKey]) => [
        orderKey,
        fields[formKey],
      ]),
    ) as Partial<OrderData>),
    price_netto_eur: fields.price ? Number(fields.price) : null,
  }
}

function ChangeIndicator({
  value,
  label,
}: {
  value: number | null
  label: string
}) {
  if (value === null) return <span className="text-foreground">-</span>
  if (value === 0) return <span className="text-foreground">(0.00%)</span>
  const color = value > 0 ? "text-red-500" : "text-green-500"
  const icon = value > 0 ? "+" : "-"
  return (
    <span className={color}>
      ({icon} {label})
    </span>
  )
}

interface OrdersTableProps {
  orders: OrderData[]
  drivers: Participant[]
  onNavigateToChat: (jid: string) => void
}

type SortOption = "date" | "distanceDiff"

export function OrdersTable({
  orders,
  drivers,
  onNavigateToChat,
}: OrdersTableProps) {
  const { t } = useTranslation()
  // Today's report dates are pl-PL; the helper keeps that in legacy mode.
  const dateLocale = getIntlLocale("pl-PL")
  const [rowsPerPage, setRowsPerPage] = useState("10")
  const [currentPage, setCurrentPage] = useState(1)
  const [isMapDisplayed, setIsMapDisplayed] = useState(false)
  const [mapOrder, setMapOrder] = useState<OrderData | null>(null)
  const [sortOption, setSortOption] = useState<SortOption>("date")
  const [completedOnly, setCompletedOnly] = useState(false)
  const driverAccountChatsMap = useAppSelector(selectAllDriversAccountChats)
  const { client } = useContext(XmppContext)
  const sltEnabled = isSltExportEnabledVhost(client?.jid?.domain)
  const [detailsOrder, setDetailsOrder] = useState<OrderData | null>(null)
  const documentsByOrder = useGpsOrderDocuments(
    orders.flatMap((order) =>
      order.delivery_order_id ? [order.delivery_order_id] : [],
    ),
  )
  const [documentsOrder, setDocumentsOrder] = useState<Order | null>(null)
  const preview = useCmrDocumentPreview()
  // The modal dialog blocks clicks outside it and closes on Escape, so it
  // steps aside while a preview is open and comes back when the preview closes.
  const previewOpen =
    (preview.galleryVisible && preview.ownsGallery) ||
    (preview.pdfVisible && preview.ownsPdf)
  const openDocument = (
    document: GpsOrderDocument,
    documents: GpsOrderDocument[],
  ) => {
    // The gallery pages through all the order's images.
    if (isPDF(document.fileUrl)) {
      preview.openPdf(document.fileUrl)
    } else {
      preview.openImage(
        document.fileUrl,
        documents
          .filter((doc) => !isPDF(doc.fileUrl))
          .map((doc) => ({ url: doc.fileUrl })),
      )
    }
  }
  const detailsSeedFields = useMemo(
    () => (detailsOrder ? buildSeedFields(detailsOrder) : undefined),
    [detailsOrder],
  )
  // Keyed by delivery_order_id (the only stable per-row id we have — Order.id
  // falls back to the row's position without it). Patched in on save so the
  // row reflects the server response immediately, no refetch of the report.
  const [orderOverrides, setOrderOverrides] = useState<
    Record<string, Partial<OrderData>>
  >({})
  const handleOrderSaved = (data: LatestDeliveryOrder) => {
    const id = detailsOrder?.delivery_order_id
    if (!id) return
    setOrderOverrides((prev) => ({
      ...prev,
      [id]: fieldsToOrderPatch(data.fields),
    }))
  }

  // Create a map of chat JID to chat name for quick lookup
  // Use the original JID from AccountChat.jid as the key (not the transformed map key)
  const chatNameMap = useMemo(() => {
    const map = new Map<string, string>()
    driverAccountChatsMap.forEach((chat) => {
      map.set(chat.jid, chat.name)
    })
    return map
  }, [driverAccountChatsMap])

  const transformedOrders = useMemo(() => {
    const patched = orders.map((order) => {
      const override = order.delivery_order_id
        ? orderOverrides[order.delivery_order_id]
        : undefined
      return override ? { ...order, ...override } : order
    })
    const transformed = patched
      .map((order, index) => transformOrderData(order, index, dateLocale))
      .filter((order) => !completedOnly || isCompletedOrder(order.apiOrder))

    if (sortOption === "distanceDiff") {
      // Sort by biggest absolute distance difference
      return transformed.sort((a, b) => {
        const diffA =
          a.apiOrder.actual_distance != null &&
          a.apiOrder.expected_distance != null
            ? Math.abs(
                a.apiOrder.actual_distance - a.apiOrder.expected_distance,
              )
            : 0
        const diffB =
          b.apiOrder.actual_distance != null &&
          b.apiOrder.expected_distance != null
            ? Math.abs(
                b.apiOrder.actual_distance - b.apiOrder.expected_distance,
              )
            : 0
        return diffB - diffA
      })
    }

    // Default: Sort by date descending (newest first)
    return transformed.sort(
      (a, b) => b.endTimestamp.getTime() - a.endTimestamp.getTime(),
    )
  }, [orders, sortOption, orderOverrides, completedOnly, dateLocale])

  const itemsPerPage = Number.parseInt(rowsPerPage)
  const totalPages = Math.max(
    1,
    Math.ceil(transformedOrders.length / itemsPerPage),
  )

  // Reset to page 1 when orders, the sort option or the filter change
  useEffect(() => {
    setCurrentPage(1)
  }, [transformedOrders.length, sortOption, completedOnly])

  const paginatedOrders = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    return transformedOrders.slice(startIndex, endIndex)
  }, [transformedOrders, currentPage, itemsPerPage])

  const finishAlertData = useMemo(
    () => (mapOrder ? buildReportRouteAlert(mapOrder) : undefined),
    [mapOrder],
  )

  return (
    <TooltipProvider>
      {isMapDisplayed && (
        <FinishAlert
          onClose={() => setIsMapDisplayed(false)}
          providedFinishedRouteAlert={finishAlertData}
        />
      )}
      {detailsOrder && (
        <OrderDetailsDialog
          driverJid={detailsOrder.chat_jid}
          deliveryOrderId={detailsOrder.delivery_order_id ?? ""}
          isOpen={true}
          onClose={() => setDetailsOrder(null)}
          seedFields={detailsSeedFields}
          onSaved={handleOrderSaved}
        />
      )}
      {documentsOrder && !previewOpen && (
        <OrderDocumentsDialog
          orderName={documentsOrder.orderName}
          documents={
            documentsByOrder[documentsOrder.apiOrder.delivery_order_id ?? ""] ??
            []
          }
          onClose={() => setDocumentsOrder(null)}
          onOpenDocument={(document) =>
            openDocument(
              document,
              documentsByOrder[
                documentsOrder.apiOrder.delivery_order_id ?? ""
              ] ?? [],
            )
          }
        />
      )}
      {preview.galleryVisible && preview.ownsGallery && (
        <ImageGallery {...preview.galleryProps} />
      )}
      {preview.pdfVisible && preview.ownsPdf && (
        <PDFViewer {...preview.pdfProps} />
      )}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold">
            {t("orders.report.ordersTable.title")}
          </h2>
          <div className="flex items-center gap-2">
            <label className="mr-4 flex items-center gap-2 text-sm text-muted-foreground">
              <Switch
                checked={completedOnly}
                onCheckedChange={setCompletedOnly}
              />
              {t("orders.report.ordersTable.completedOnly")}
            </label>
            <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              {t("orders.report.ordersTable.sortBy")}
            </span>
            <Select
              value={sortOption}
              onValueChange={(value: SortOption) => setSortOption(value)}
            >
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="date">
                  {t("orders.report.ordersTable.sortDate")}
                </SelectItem>
                <SelectItem value="distanceDiff">
                  {t("orders.report.ordersTable.sortDistanceDiff")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="overflow-hidden rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted">
                <TableHead className="border-r font-semibold">
                  {t("orders.report.ordersTable.columns.driver")}
                </TableHead>
                <TableHead className="border-r font-semibold">
                  {t("orders.report.ordersTable.columns.order")}
                </TableHead>
                {sltEnabled && (
                  <>
                    {/* Labelled after the field it renders (order_number), not
                        after the SLT export element that carries it — the
                        export ships the order number inside <reference-number>,
                        which is what the old "Reference number" header came
                        from. The dialog's separate Reference number field
                        (ref_number) has no column here. */}
                    <TableHead className="border-r font-semibold">
                      {t("orders.report.ordersTable.columns.orderNumber")}
                    </TableHead>
                    <TableHead className="border-r font-semibold">
                      {t("orders.report.ordersTable.columns.vatId")}
                    </TableHead>
                  </>
                )}
                <TableHead className="border-r font-semibold">
                  {t("orders.report.ordersTable.columns.date")}
                </TableHead>
                <TableHead
                  colSpan={3}
                  className="border-r text-center font-semibold"
                >
                  {t("orders.report.ordersTable.columns.actual")}
                </TableHead>
                <TableHead
                  colSpan={3}
                  className="border-r text-center font-semibold"
                >
                  {t("orders.report.ordersTable.columns.approach")}
                </TableHead>
                <TableHead
                  colSpan={2}
                  className="border-r text-center font-semibold"
                >
                  {t("orders.report.ordersTable.columns.summary")}
                </TableHead>
                {sltEnabled && (
                  <TableHead className="border-r text-center font-semibold">
                    {t("orders.report.ordersTable.columns.export")}
                  </TableHead>
                )}
                <TableHead className="text-center font-semibold">
                  {t("orders.report.ordersTable.columns.actions")}
                </TableHead>
              </TableRow>
              <TableRow className="bg-muted">
                <TableHead className="border-r"></TableHead>
                <TableHead className="border-r"></TableHead>
                {sltEnabled && (
                  <>
                    <TableHead className="border-r"></TableHead>
                    <TableHead className="border-r"></TableHead>
                  </>
                )}
                <TableHead className="border-r"></TableHead>
                <TableHead className="border-r text-right">
                  {t("orders.report.ordersTable.columns.distance")}
                </TableHead>
                <TableHead className="border-r text-right">
                  {t("orders.report.ordersTable.columns.duration")}
                </TableHead>
                <TableHead className="border-r text-right">
                  {t("orders.report.ordersTable.columns.tolls")}
                </TableHead>
                <TableHead className="border-r text-right">
                  {t("orders.report.ordersTable.columns.distance")}
                </TableHead>
                <TableHead className="border-r text-right">
                  {t("orders.report.ordersTable.columns.duration")}
                </TableHead>
                <TableHead className="border-r text-right">
                  {t("orders.report.ordersTable.columns.tolls")}
                </TableHead>
                <TableHead className="border-r text-right">
                  {t("orders.report.ordersTable.columns.revenue")}
                </TableHead>
                <TableHead className="border-r text-right">
                  {t("orders.report.ordersTable.columns.totalDistance")}
                </TableHead>
                {sltEnabled && <TableHead className="border-r"></TableHead>}
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedOrders.map((order) => (
                <TableRow key={order.id}>
                  <TableCell className="border-r font-medium">
                    {chatNameMap.get(order.chatJid) || order.chatJid}
                  </TableCell>
                  <TableCell className="border-r font-medium">
                    {order.fileUrl ? (
                      <a
                        href={order.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 hover:underline"
                        title={order.orderName || undefined}
                      >
                        {truncateFilename(order.orderName)}
                      </a>
                    ) : (
                      <span>{truncateFilename(order.orderName)}</span>
                    )}
                    {order.apiOrder.description && (
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {order.apiOrder.description}
                      </p>
                    )}
                  </TableCell>
                  {sltEnabled && (
                    <>
                      <TableCell className="border-r font-medium">
                        {order.apiOrder?.order_number || "—"}
                      </TableCell>
                      <TableCell className="border-r font-medium">
                        {order.apiOrder?.customer_nip || "—"}
                      </TableCell>
                    </>
                  )}
                  <TableCell className="border-r font-medium">
                    {order.date}
                  </TableCell>
                  <TableCell className="border-r text-right font-mono">
                    <div className="flex flex-col text-sm">
                      <ChangeIndicator
                        value={order.actualDistanceChangePercentage}
                        label={order.actualDistanceChange}
                      />
                      <span>{order.actualDistance}</span>
                    </div>
                  </TableCell>
                  <TableCell className="border-r text-right font-mono">
                    <div className="flex flex-col text-sm">
                      <ChangeIndicator
                        value={order.actualDurationChangePercentage}
                        label={order.actualDurationChange}
                      />
                      <span>{order.actualDuration}</span>
                    </div>
                  </TableCell>
                  <TableCell className="border-r text-right font-mono">
                    <div className="flex flex-col text-sm">
                      <ChangeIndicator
                        value={order.actualCostChangePercentage}
                        label={order.actualCostChange}
                      />
                      <span>{order.actualCost}</span>
                    </div>
                  </TableCell>
                  <TableCell className="border-r text-right font-mono">
                    {order.approachDistance}
                  </TableCell>
                  <TableCell className="border-r text-right font-mono">
                    {order.approachDuration}
                  </TableCell>
                  <TableCell className="border-r text-right font-mono">
                    {order.approachCost}
                  </TableCell>
                  <TableCell className="border-r text-right font-mono">
                    {order.revenue}
                  </TableCell>
                  <TableCell className="border-r text-right font-mono">
                    {order.totalDistance}
                  </TableCell>
                  {sltEnabled && (
                    <TableCell className="border-r">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex items-center gap-1.5">
                            {order.apiOrder.export_status === "success" ? (
                              <CircleCheck className="h-4 w-4 shrink-0 text-green-600" />
                            ) : order.apiOrder.export_status === "failed" ? (
                              <CircleX className="h-4 w-4 shrink-0 text-red-600" />
                            ) : (
                              <Circle className="h-4 w-4 shrink-0 text-muted-foreground" />
                            )}
                            {order.apiOrder.export_external_id && (
                              <span className="text-xs text-muted-foreground">
                                {order.apiOrder.export_external_id}
                              </span>
                            )}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          {order.apiOrder.export_status === "success"
                            ? order.apiOrder.export_external_id
                              ? t("orders.report.ordersTable.exportedWithId", {
                                  id: order.apiOrder.export_external_id,
                                })
                              : t("orders.report.ordersTable.exported")
                            : order.apiOrder.export_status === "failed"
                              ? order.apiOrder.export_error
                                ? t(
                                    "orders.report.ordersTable.exportFailedWithError",
                                    { error: order.apiOrder.export_error },
                                  )
                                : t("orders.report.ordersTable.exportFailed")
                              : t("orders.report.ordersTable.notExported")}
                        </TooltipContent>
                      </Tooltip>
                    </TableCell>
                  )}
                  <TableCell>
                    <div className="flex items-center justify-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onNavigateToChat(order.chatJid)}
                      >
                        <MessageSquare className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setMapOrder(order.apiOrder)
                          setIsMapDisplayed(true)
                        }}
                      >
                        <MapIcon className="h-4 w-4" />
                      </Button>
                      {(documentsByOrder[order.apiOrder.delivery_order_id ?? ""]
                        ?.length ?? 0) > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          title={t("orders.orderDocuments.title")}
                          onClick={() => setDocumentsOrder(order)}
                        >
                          <Paperclip className="h-4 w-4" />
                          <span className="text-xs">
                            {
                              documentsByOrder[
                                order.apiOrder.delivery_order_id ?? ""
                              ].length
                            }
                          </span>
                        </Button>
                      )}
                      {sltEnabled && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span tabIndex={0}>
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={!order.apiOrder?.delivery_order_id}
                                onClick={() => setDetailsOrder(order.apiOrder)}
                              >
                                <FileText className="h-4 w-4" />
                              </Button>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            {order.apiOrder?.delivery_order_id
                              ? t("orders.report.ordersTable.orderDetails")
                              : t("orders.report.ordersTable.noMatchingOrder")}
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="flex items-center justify-end">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {t("orders.report.ordersTable.rowsPerPage")}
              </span>
              <Select value={rowsPerPage} onValueChange={setRowsPerPage}>
                <SelectTrigger className="w-16">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5</SelectItem>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="text-sm text-muted-foreground">
              {t("orders.report.ordersTable.pageOf", {
                current: currentPage,
                total: totalPages,
              })}
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              >
                {"<<"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              >
                {"<"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setCurrentPage(Math.min(totalPages, currentPage + 1))
                }
                disabled={currentPage === totalPages}
              >
                {">"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
              >
                {">>"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </TooltipProvider>
  )
}
